import { roundBudgetMoney } from "../budgetFringeTotals";
import { formatBudgetMoney } from "../formatBudgetMoney";
import { ComparisonRowView, ComparisonScenario } from "./BudgetScenarioCompareModal.types";

/**
 * Decorates each scenario with its delta vs the baseline (current budget) and
 * flags the lowest/highest totals so the table can highlight the spread.
 */
export const buildComparisonRows = (scenarios: ComparisonScenario[]): ComparisonRowView[] => {
  if (scenarios.length === 0) return [];

  const baseline = scenarios.find((scenario) => scenario.kind === "baseline") ?? scenarios[0];
  const baselineTotal = baseline.total;

  const totals = scenarios.map((scenario) => scenario.total);
  const min = Math.min(...totals);
  const max = Math.max(...totals);
  const hasSpread = scenarios.length > 1 && min !== max;

  return scenarios.map((scenario) => {
    const delta = scenario.total - baselineTotal;
    const isBaseline = scenario.kind === "baseline";
    return {
      ...scenario,
      delta,
      deltaPct: baselineTotal !== 0 ? (delta / baselineTotal) * 100 : null,
      isBaseline,
      isLowest: hasSpread && !isBaseline && scenario.total === min,
      isHighest: hasSpread && !isBaseline && scenario.total === max,
    };
  });
};

/** Absolute money, e.g. `$1,250,000.00`. */
export const formatMoney = (currencySymbol: string, value: number): string =>
  formatBudgetMoney(currencySymbol, value);

/** Signed money for deltas, e.g. `+$10,000.00` / `-$5,000.00` / `$0.00`. */
export const formatSignedMoney = (currencySymbol: string, value: number): string => {
  const rounded = roundBudgetMoney(value);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "-" : "";
  return `${sign}${formatBudgetMoney(currencySymbol, Math.abs(rounded))}`;
};

/** Signed percentage for deltas, e.g. `+4.2%`. Returns null-safe dash for null. */
export const formatSignedPercent = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};
